import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { Field } from "@/app/tool/Options";

const num = (v) => (v === "" || v === null ? null : Number(v));

function PlanEditor({ p, iyzicoReady, onSaved }) {
  const { t, errMsg } = useI18n();
  const [f, setF] = useState(p);
  useEffect(() => setF(p), [p]);
  const save = async () => {
    try {
      const body = { name: f.name, price_monthly: num(f.price_monthly) ?? 0, price_yearly: num(f.price_yearly), credits_per_month: num(f.credits_per_month) ?? 0,
        is_active: f.is_active, popular: f.popular, iyzico_ref_monthly: f.iyzico_ref_monthly || "", iyzico_ref_yearly: f.iyzico_ref_yearly || "" };
      if (body.price_monthly !== p.price_monthly && f.iyzico_ref_monthly === p.iyzico_ref_monthly) delete body.iyzico_ref_monthly;
      if (body.price_yearly !== p.price_yearly && f.iyzico_ref_yearly === p.iyzico_ref_yearly) delete body.iyzico_ref_yearly;
      onSaved((await api.patch(`/admin/plans/${p.id}`, body)).data.items);
      toast.success(t("aplans.saved"));
    } catch (e) { setF((x) => ({ ...x, is_active: p.is_active })); toast.error(errMsg(e)); }
  };
  const create = async (interval) => {
    try { onSaved((await api.post(`/admin/plans/${p.id}/iyzico`, null, { params: { interval } })).data.items); toast.success(t("settings.saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k, props = {}) => <Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`aplan-${p.id}-${k}`} {...props} />;
  return (
    <div className={`panel p-5 space-y-3 ${f.popular ? "border-emerald-600" : ""}`} data-testid={`aplan-card-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs text-neutral-500">{p.id}</span>
        <label className="flex items-center gap-2 text-xs"><Switch checked={!!f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid={`aplan-${p.id}-active`} />{t("aplans.active")}</label>
      </div>
      <Field label={t("aplans.name")}>{inp("name", { maxLength: 40 })}</Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`${t("aplans.priceMonthly")} (${p.currency})`}>{inp("price_monthly", { type: "number", min: 0 })}</Field>
        <Field label={t("aplans.credits")}>{inp("credits_per_month", { type: "number", min: 0 })}</Field>
      </div>
      <Field label={`${t("aplans.priceYearly")} (${p.currency})`}>{inp("price_yearly", { type: "number", min: 0 })}</Field>
      <label className="flex items-center gap-2 text-xs"><Switch checked={!!f.popular} onCheckedChange={(v) => setF({ ...f, popular: v })} data-testid={`aplan-${p.id}-popular`} />{t("aplans.popular")}</label>
      {["monthly", "yearly"].map((iv) => (
        <Field key={iv} label={t(iv === "monthly" ? "aplans.refMonthly" : "aplans.refYearly")}>
          <div className="flex gap-2">
            {inp(`iyzico_ref_${iv}`, { className: "font-mono text-xs", maxLength: 100 })}
            <button type="button" onClick={() => create(iv)} disabled={!iyzicoReady || !(iv === "monthly" ? p.price_monthly : p.price_yearly)}
              className="shrink-0 rounded-full border border-emerald-600 text-emerald-400 px-3 text-xs disabled:opacity-40" data-testid={`aplan-${p.id}-create-${iv}`}>{t("aplans.create")}</button>
          </div>
        </Field>
      ))}
      <button onClick={save} className="btn-brand w-full rounded-full py-2 text-sm font-semibold" data-testid={`aplan-${p.id}-save`}>{t("common.save")}</button>
    </div>
  );
}

export default function PlansTab({ iyzicoReady }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/plans").then((r) => setItems(r.data.items)); }, []);
  return (
    <div className="space-y-5" data-testid="admin-plans-tab">
      <p className="text-sm text-neutral-400 max-w-3xl">{t("aplans.intro")}</p>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {(items || []).map((p) => <PlanEditor key={p.id} p={p} iyzicoReady={iyzicoReady} onSaved={setItems} />)}
      </div>
    </div>
  );
}
